import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Text } from "../../../../components/common/typography";
import { StatusChip, type OrderStatus } from "../../../../components/common/chip";
import { SimpleTable, type SimpleTableColumn } from "../../../../components/common/simple-table";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer";
import { Pagination } from "../../../../components/common/pagination";
import { Button } from "../../../../components/common/button";
import { SectionCard } from "../../../../components/common/section-card";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { usePartnerSales } from "../../hooks/usePartnerSales";
import { usePartnerPurchaseOrders } from "../../hooks/usePartnerPurchaseOrders";
import type { SaleDto, PurchaseOrderDto } from "../../../../api";
import { deriveOrderStatus, formatLongDate } from "../../../sales/utils/order-status";

const PAGE_SIZE = 10;

const DATE_FMT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FMT.format(d);
}

interface PartnerOrdersTabProps {
  partnerId: number;
  isSeller: boolean;
}

export function PartnerOrdersTab({ partnerId, isSeller }: PartnerOrdersTabProps) {
  const navigate = useNavigate();
  const [salesPage, setSalesPage] = useState(1);
  const [poPage, setPoPage] = useState(1);

  const { data: salesData, isLoading: salesLoading } = usePartnerSales(partnerId, { page: salesPage, pageSize: PAGE_SIZE });
  const { data: poData, isLoading: poLoading } = usePartnerPurchaseOrders(partnerId, { page: poPage, pageSize: PAGE_SIZE });

  const salesColumns = useMemo((): SimpleTableColumn<SaleDto>[] => [
    {
      key: "name",
      label: "Order",
      className: "flex-[2] min-w-0",
      render: (row) => <Text variant="bodyBold" tone="dark">{row.name}</Text>,
    },
    {
      key: "date",
      label: "Date",
      className: "flex-1",
      render: (row) => <Text variant="body" tone="body">{fmtDate(row.dateOrder)}</Text>,
    },
    {
      key: "amount",
      label: "Total (QAR)",
      className: "flex-1",
      render: (row) => (
        <Text variant="bodyBold" tone="dark">
          {row.amountTotal != null ? row.amountTotal.toLocaleString("en-US", { maximumFractionDigits: 0 }) : "—"}
        </Text>
      ),
    },
    {
      key: "status",
      label: "Status",
      className: "flex-1",
      render: (row) => <StatusChip status={deriveOrderStatus(row) as OrderStatus} />,
    },
    {
      key: "actions",
      label: "Actions",
      className: "flex-1 flex justify-end",
      render: (row) => (
        <Button variant="ghost" size="sm" className="!px-0 !text-textDark"
          onClick={(e) => { e.stopPropagation(); navigate(AppRoutes.ADMIN_ORDER_DETAILS.replace(":id", String(row.id))); }}>
          View
        </Button>
      ),
    },
  ], [navigate]);

  const poColumns = useMemo((): SimpleTableColumn<PurchaseOrderDto>[] => [
    {
      key: "name",
      label: "PO",
      className: "flex-[2] min-w-0",
      render: (row) => <Text variant="bodyBold" tone="dark">{row.name}</Text>,
    },
    {
      key: "date",
      label: "Date",
      className: "flex-1",
      render: (row) => <Text variant="body" tone="body">{formatLongDate(row.dateOrder)}</Text>,
    },
    {
      key: "amount",
      label: "Total (QAR)",
      className: "flex-1",
      render: (row) => (
        <Text variant="bodyBold" tone="dark">
          {row.amountTotal != null ? row.amountTotal.toLocaleString("en-US", { maximumFractionDigits: 0 }) : "—"}
        </Text>
      ),
    },
    {
      key: "status",
      label: "Status",
      className: "flex-1",
      render: (row) => <Text variant="body" tone="body">{row.state ?? "—"}</Text>,
    },
    {
      key: "actions",
      label: "Actions",
      className: "flex-1 flex justify-end",
      render: (row) => (
        <Button variant="ghost" size="sm" className="!px-0 !text-textDark"
          onClick={(e) => { e.stopPropagation(); navigate(AppRoutes.ADMIN_PURCHASE_ORDER_DETAILS.replace(":id", String(row.id))); }}>
          View
        </Button>
      ),
    },
  ], [navigate]);

  const salesPageCount = salesData?.meta?.pageCount ?? 1;
  const poPageCount = poData?.meta?.pageCount ?? 1;

  return (
    <div className="flex flex-col gap-6">
      <SectionCard title={isSeller ? "Sale Orders" : "Orders"}>
        {salesLoading ? (
          <ShimmerLoader message="Fetching orders…">
            <div className="flex flex-col gap-2">{Array.from({ length: 5 }).map((_, i) => <Shimmer key={i} className="h-12 w-full" />)}</div>
          </ShimmerLoader>
        ) : (
          <SimpleTable
            columns={salesColumns}
            data={salesData?.data ?? []}
            keyExtractor={(o) => String(o.id)}
            onRowClick={(o) => navigate(AppRoutes.ADMIN_ORDER_DETAILS.replace(":id", String(o.id)))}
            footer={salesPageCount > 1 ? (
              <div className="flex justify-center"><Pagination page={salesPage} pageCount={salesPageCount} onPageChange={setSalesPage} /></div>
            ) : undefined}
          />
        )}
      </SectionCard>

      {isSeller && (
        <SectionCard title="Purchase Orders">
          {poLoading ? (
            <ShimmerLoader message="Fetching purchase orders…">
              <div className="flex flex-col gap-2">{Array.from({ length: 5 }).map((_, i) => <Shimmer key={i} className="h-12 w-full" />)}</div>
            </ShimmerLoader>
          ) : (
            <SimpleTable
              columns={poColumns}
              data={poData?.data ?? []}
              keyExtractor={(o) => String(o.id)}
              footer={poPageCount > 1 ? (
                <div className="flex justify-center"><Pagination page={poPage} pageCount={poPageCount} onPageChange={setPoPage} /></div>
              ) : undefined}
            />
          )}
        </SectionCard>
      )}
    </div>
  );
}
